import {
  useCallback, useRef, useState, useTransition,
} from 'react';
import type { DisabledState } from '~/_shared/types/disabledState';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import {
  isDriveTimePolygon, isIndividualRadius, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  AGS_REPORT_MAX_RADIUS_IN_MILES, validatePolygonForAgsApi, validateRadiusForAgsApi,
} from '~/exportReport/validation/exportReportApiValidation.helpers';
import { getExportReportAreas } from '~/exportReport/validation/getExportReportAreas';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';

const DISABLED_DEFAULT: DisabledState = { isDisabled: true, reason: '' };
// A failed area lookup must not block the user. We will allow user to select the area.
const VALIDATION_FAILED: DisabledState = { isDisabled: false };

const useValidationRunner = () => {
  const [isPending, startTransition] = useTransition();
  const [validation, setValidation] = useState<DisabledState>(DISABLED_DEFAULT);
  const latestRequestIdRef = useRef(0);

  // Keeps the most recently started validation authoritative and never rejects, so awaiting the result is always safe.
  const run = useCallback((task: () => Promise<DisabledState>): Promise<DisabledState> => {
    const requestId = ++latestRequestIdRef.current;
    const promise = task().catch(() => VALIDATION_FAILED);

    startTransition(async () => {
      const result = await promise;

      if (requestId === latestRequestIdRef.current) {
        setValidation(result);
      }
    });

    return promise;
  }, []);

  return { isPending, validation, run };
};

export const useExportReportValidation = () => {
  const [t] = useTranslation();
  const latLngLookup = useLatLngSpreadsheetData();
  const { isPending: isPolygonPending, validation: polygonValidation, run: runPolygonValidation } = useValidationRunner();
  const { isPending: isProximityPending, validation: proximityValidation, run: runProximityValidation } = useValidationRunner();
  const { isPending: isLocationPending, validation: locationValidation, run: runLocationValidation } = useValidationRunner();

  const validateReportAreasContainingPolygon = useCallback((multiPolygon: MultiPolygon): Promise<DisabledState> => {
    const polygonWithoutHoles: MultiPolygon = multiPolygon.map(polygon => ({
      path: polygon.path,
      holes: [],
    }));

    return getExportReportAreas({ type: 'polygon', paths: polygonWithoutHoles })
      .then((reportAreas): DisabledState => {
        return reportAreas.length
          ? { isDisabled: false }
          : { isDisabled: true, reason: t('exportReport.unavailableInTheArea') };
      });
  }, [t]);

  const validateReportMultiPolygon = useCallback((multiPolygon: MultiPolygon): Promise<DisabledState> => (
    runPolygonValidation(async () => {
      const polygonValidationResult = validatePolygonForAgsApi(multiPolygon);

      if (!polygonValidationResult.isValid) {
        return {
          isDisabled: true,
          reason: t(polygonValidationResult.reason),
        };
      }

      return validateReportAreasContainingPolygon(multiPolygon);
    })
  ), [runPolygonValidation, t, validateReportAreasContainingPolygon]);

  const polygonValidationResult: DisabledState = isPolygonPending
    ? { isDisabled: true, reason: t('Loading...') }
    : polygonValidation;

  const validateReportProximity = useCallback((proximity: Proximity, spreadsheetRowId?: SpreadsheetRowId): Promise<DisabledState> => {
    let center: { lat: number; lng: number } | undefined;

    if (isDriveTimePolygon(proximity)) {
      center = {
        lat: proximity.data.lat,
        lng: proximity.data.lng,
      };
    }
    else if (isIndividualRadius(proximity)) {
      center = {
        lat: proximity.data.data.lat,
        lng: proximity.data.data.lng,
      };
    }

    if (spreadsheetRowId) {
      const spreadsheetData = latLngLookup.getRow(spreadsheetRowId);

      if (spreadsheetData) {
        center = {
          lat: spreadsheetData.lat,
          lng: spreadsheetData.lng,
        };
      }
    }

    const validateDistance = (): DisabledState => {
      if (!validateRadiusForAgsApi(proximity, spreadsheetRowId)) {
        return {
          isDisabled: true,
          reason: t('Summary unavailable over {{miles}} miles', {
            miles: AGS_REPORT_MAX_RADIUS_IN_MILES,
          }),
        };
      }

      return { isDisabled: false };
    };

    return runProximityValidation(async () => {
      if (!center) {
        return validateDistance();
      }

      const reportAreas = await getExportReportAreas({ type: 'point', point: center });

      return reportAreas.length
        ? validateDistance()
        : { isDisabled: true, reason: t('exportReport.notSupportedArea.radii') };
    });
  }, [runProximityValidation, t, latLngLookup]);

  const proximityValidationResult: DisabledState = isProximityPending
    ? { isDisabled: true, reason: t('Loading...') }
    : proximityValidation;

  const validateReportLocation = useCallback((spreadsheetRowId: SpreadsheetRowId): Promise<DisabledState> => (
    runLocationValidation(async () => {
      const center = latLngLookup.getRow(spreadsheetRowId);

      if (!center) {
        return { isDisabled: false };
      }

      const reportAreas = await getExportReportAreas({ type: 'point', point: { lat: center.lat, lng: center.lng } });

      return reportAreas.length
        ? { isDisabled: false }
        : { isDisabled: true, reason: t('exportReport.unavailableInTheArea') };
    })
  ), [runLocationValidation, t, latLngLookup]);

  const locationValidationResult: DisabledState = isLocationPending
    ? { isDisabled: true, reason: t('Loading...') }
    : locationValidation;

  return {
    validateReportProximity,
    validateReportMultiPolygon,
    validateReportLocation,
    polygonValidationResult,
    proximityValidationResult,
    locationValidationResult,
  };
};
